import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export type ConflictEvent = Tables<"events">;
export type NewsItem = Tables<"news_items">;
export type Product = Tables<"products">;

// Trigger edge functions to refresh data (fire and forget, once per session)
let _triggered = false;
const triggerDataRefresh = () => {
  if (_triggered) return;
  _triggered = true;
  const functions = ["fetch-events", "fetch-hdx", "fetch-conflict-rss", "fetch-gnews", "fetch-ukraine-alerts"];
  for (const fn of functions) {
    supabase.functions.invoke(fn).catch(() => {});
  }
};

export const useEvents = (filters?: {
  eventType?: string;
  topic?: string;
}, isPremium = false) => {
  triggerDataRefresh();

  return useQuery({
    queryKey: ["events", filters, isPremium],
    queryFn: async () => {
      let query = supabase
        .from("events")
        .select("*")
        .order("occurred_at", { ascending: false });

      // Free users: last 24h only, max 20 events
      if (!isPremium) {
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        query = query.gte("occurred_at", oneDayAgo).limit(20);
      } else {
        query = query.limit(500);
      }

      if (filters?.eventType && filters.eventType !== "all") {
        query = query.eq("event_type", filters.eventType as any);
      }

      const { data, error } = await query;
      if (error) throw error;
      if ((data?.length ?? 0) === 0) {
        // Fallback: no recent events — show the latest recorded events instead of an empty state
        let fb = supabase.from("events").select("*").order("occurred_at", { ascending: false }).limit(isPremium ? 100 : 20);
        if (filters?.eventType && filters.eventType !== "all") fb = fb.eq("event_type", filters.eventType as any);
        const { data: latest } = await fb;
        return latest ?? [];
      }
      return data ?? [];
    },
    refetchInterval: isPremium ? 30000 : 300000,
    staleTime: 60000, // Data is fresh for 1 min
    retry: 2,
    placeholderData: (prev) => prev ?? [],
  });
};

export const useNewsItems = (topic?: string, isPremium = false) => {
  return useQuery({
    queryKey: ["news_items", topic, isPremium],
    queryFn: async () => {
      let query = supabase
        .from("news_items")
        .select("*")
        .order("published_at", { ascending: false });

      // Free users: max 5 articles with 1h delay
      if (!isPremium) {
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
        query = query.lte("published_at", oneHourAgo).limit(5);
      } else {
        query = query.limit(50);
      }

      if (topic && topic !== "all") {
        query = query.eq("topic", topic as any);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: isPremium ? 30000 : 300000,
    staleTime: 60000,
    retry: 2,
    placeholderData: (prev) => prev ?? [],
  });
};

export const useProducts = () => {
  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("price_usd", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
};

export const useDonationStats = () => {
  return useQuery({
    queryKey: ["donation_stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("donations")
        .select("cause, amount_usd");
      if (error) throw error;

      const stats = {
        ukraine: { raised: 0, goal: 40000 },
        gaza: { raised: 0, goal: 30000 },
      };

      data?.forEach((d) => {
        if (d.cause === "ukraine") stats.ukraine.raised += Number(d.amount_usd);
        if (d.cause === "gaza") stats.gaza.raised += Number(d.amount_usd);
      });

      return stats;
    },
    refetchInterval: 30000,
  });
};
